import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";

export default function ImportFromUrls({ open, onOpenChange, onImported }) {
  const api = useApi();
  const [urlsText, setUrlsText] = useState("");
  const [tags, setTags] = useState("");
  const [collection, setCollection] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setUrlsText(""); setTags(""); setCollection(""); };

  const submit = async () => {
    const urls = urlsText.split(/[\n,\s]+/).map((u) => u.trim()).filter(Boolean);
    if (urls.length === 0) { toast.error("Paste at least one image URL"); return; }
    setBusy(true);
    try {
      const res = await api.post("/media/import-urls", { urls, tags: tags.trim() || undefined, collection: collection.trim() || undefined });
      const { imported, errors } = res.data;
      if (imported > 0) toast.success(`Imported ${imported} image${imported > 1 ? "s" : ""} to your library`);
      if (errors && errors.length) toast.error(`${errors.length} link${errors.length > 1 ? "s" : ""} skipped (see details)`);
      if (imported === 0 && (!errors || !errors.length)) toast.error("Nothing was imported");
      reset();
      onOpenChange(false);
      onImported && onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="import-urls-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Paste image links</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#7A9090]">Paste one or more public image URLs — we'll download, check them, and add them to your Media Library. One per line (or comma-separated).</p>
          <div className="space-y-1.5">
            <Label className="text-[#3C4048]">Image URLs</Label>
            <Textarea
              data-testid="import-urls-input"
              value={urlsText}
              onChange={(e) => setUrlsText(e.target.value)}
              placeholder={"https://example.com/photo-1.jpg\nhttps://example.com/photo-2.png"}
              className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048] min-h-[120px] text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#3C4048]">Collection / campaign (optional)</Label>
            <Input
              data-testid="import-urls-collection"
              value={collection}
              onChange={(e) => setCollection(e.target.value)}
              placeholder="e.g. Summer 2026 Launch"
              className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#3C4048]">Tags (optional)</Label>
            <Input
              data-testid="import-urls-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="e.g. hero, summer-campaign"
              className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048]"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-[12px]" data-testid="import-urls-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[12px] gap-2" data-testid="import-urls-submit">
              <LinkSimple size={18} /> {busy ? "Importing…" : "Import"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
